import type { YextPublisherId } from ':modules/yext';

import {
    mapLanguageStringsToISO,
    NfcsPlatformKey,
    PlatformCategory,
    type PlatformDefinition,
    PlatformKey,
    platformsKeys,
    ReplyOption,
    ReviewRetryPolicy,
    SocialNetworkKey,
} from ':constants/index';
import {
    changeUrlDomain,
    filterByRequiredKeys,
    isNotNil,
    isValidFacebookUrl,
    isValidInstagramUrl,
    isValidLinkedinUrl,
    isValidPinterestUrl,
    isValidTiktokUrl,
    isValidXUrl,
    isValidYoutubeUrl,
    transformFacebookUrl,
    transformInstagramUrl,
    transformLinkedinUrl,
    transformPinterestUrl,
    transformTiktokUrl,
    transformXUrl,
    transformYoutubeUrl,
} from ':helpers/index';
import { ReviewReplyAutomationComment } from ':modules/automations';

export const getPlatformDefinitions = (): PlatformDefinition[] => Object.values(platformsKeys);

export const getPlatformDefinition = (platformKey: string): PlatformDefinition | undefined =>
    Object.entries(platformsKeys).find(([key, platform]) => key === platformKey || platform.key === platformKey)?.[1];

// Configs
export const getNonPrivatePlatforms = (): PlatformDefinition[] =>
    getPlatformDefinitions().filter((platform) => platform.category !== PlatformCategory.PRIVATE);

export const getFeatureFlaggedPlatforms = () => filterByRequiredKeys(getPlatformDefinitions(), ['featureFlagKey']);

export const getNonFeatureFlaggedPlatforms = (): PlatformDefinition[] =>
    getPlatformDefinitions().filter((platform) => !platform.featureFlagKey);

export const getPlatformsWithOauth = (): PlatformDefinition[] => getPlatformDefinitions().filter((platform) => platform.oauth);

export const isOauthPlatform = (key: string): boolean => getPlatformDefinition(key)?.oauth ?? false;

export const getPlatformsWithShouldFetchPermissions = (): PlatformDefinition[] =>
    getPlatformDefinitions().filter((platform) => platform.shouldFetchPermissions);

export const doesPlatformHasAutoAccessValidation = (key: PlatformKey): boolean =>
    getPlatformDefinitions().find((platform) => platform.key === key)?.hasAutoAccessValidation ?? false;

export const getPlatformsKeysWithShouldDetectReviewsLang = (): PlatformKey[] =>
    getPlatformDefinitions()
        .filter((platform) => platform.shouldDetectReviewsLang)
        .map((platform) => platform.key);

// Platform types
export const getSocialPlatformKeys = (): PlatformKey[] =>
    getPlatformDefinitions()
        .filter((platform) => platform.category === PlatformCategory.SOCIAL)
        .map((platform) => platform.key);

export const getDeliveryPlatformKeys = (): PlatformKey[] =>
    getPlatformDefinitions()
        .filter((platform) => platform.category === PlatformCategory.DELIVERY)
        .map((platform) => platform.key);

export const getPlatformKeysLinkedWithMeta = (): PlatformKey[] =>
    getPlatformDefinitions()
        .filter((platform) => [PlatformKey.FACEBOOK, PlatformKey.INSTAGRAM].includes(platform.key))
        .map((platform) => platform.key);

// Posts
export const getSeoPlatformKeysWithPost = (): PlatformKey[] =>
    getPlatformDefinitions()
        .filter((platform) => platform.hasPosts && platform.category === PlatformCategory.SEO)
        .map((platform) => platform.key);

export const getSocialPlatformKeysWithPost = (): PlatformKey[] =>
    getPlatformDefinitions()
        .filter((platform) => platform.hasPosts && platform.category === PlatformCategory.SOCIAL)
        .map((platform) => platform.key);

export const getSocialDefaultPlatformKeysForPost = (): PlatformKey[] =>
    getSocialPlatformKeysWithPost().filter((key) => key !== PlatformKey.MAPSTR);

export const getPlatformKeysWithMentions = (): PlatformKey[] =>
    getPlatformDefinitions()
        .filter((platform) => platform.hasMentions)
        .map((platform) => platform.key);

export const getPlatformKeysWithStories = (): PlatformKey[] =>
    getPlatformDefinitions()
        .filter((platform) => platform.hasStory)
        .map((platform) => platform.key);

export const getSocialPlatformKeysWithDuplicablePosts = (): PlatformKey[] =>
    getPlatformDefinitions()
        .filter((platform) => platform.category === PlatformCategory.SOCIAL && platform.hasPosts && platform.hasDuplicablePosts)
        .map((platform) => platform.key);

export const getSeoPlatformKeysWithDuplicablePosts = (): PlatformKey[] =>
    getPlatformDefinitions()
        .filter((platform) => platform.category === PlatformCategory.SEO && platform.hasPosts && platform.hasDuplicablePosts)
        .map((platform) => platform.key);

export const getPlatformKeysWithReel = (): PlatformKey[] =>
    getPlatformDefinitions()
        .filter((platform) => platform.hasReel)
        .map((platform) => platform.key);

export const getPlatformKeysWithFeed = (): PlatformKey[] =>
    getPlatformDefinitions()
        .filter((platform) => platform.hasFeed)
        .map((platform) => platform.key);

export const canDeleteStory = (key: string): boolean => getPlatformDefinition(key)?.canDeleteStory ?? true;

// Stats
export const getPlatformKeysWithFollowers = (): PlatformKey[] =>
    getPlatformDefinitions()
        .filter((platform) => platform.hasFollowers)
        .map((platform) => platform.key);

export const getPlatformKeysWithRSStats = (): PlatformKey[] =>
    getPlatformDefinitions()
        .filter((platform) => platform.hasRSStats)
        .map((platform) => platform.key);

export const getPlatformKeysWithKeywordSearchImpressions = (): PlatformKey[] =>
    getPlatformDefinitions()
        .filter((platform) => platform.hasKeywordSearchImpressions)
        .map((platform) => platform.key);

// Informations
export const getPlatformKeysWithCategories = (): PlatformKey[] =>
    getPlatformDefinitions()
        .filter((platform) => platform.hasInformationCategories)
        .map((platform) => platform.key);

export const getPlatformKeysWithHours = (): PlatformKey[] =>
    getPlatformDefinitions()
        .filter((platform) => platform.hasInformationHours)
        .map((platform) => platform.key);

export const getPlatformKeysWithSuggestions = (): PlatformKey[] =>
    getPlatformDefinitions()
        .filter((platform) => platform.hasInformationSuggestions)
        .map((platform) => platform.key);

export const shouldCompareInformation = (key: string): boolean => getPlatformDefinition(key)?.shouldCompareInformation ?? false;

export const getPlatformKeysWithYextPublisherIds = (): YextPublisherId[] =>
    getPlatformDefinitions()
        .filter((platform) => platform.yextPublisherId)
        .map((platform) => platform.yextPublisherId as YextPublisherId);

export const getPlatformKeysForYextPublisherIds = (yextPublisherIds: string[]): PlatformKey[] =>
    getPlatformDefinitions()
        .filter((platform) => yextPublisherIds.includes(platform.yextPublisherId ?? ''))
        .map((platform) => platform.key);

// Reviews
export const getPlatformKeysThatCanBeReplied = (withOrWithoutComment: ReviewReplyAutomationComment): PlatformKey[] =>
    getPlatformDefinitions()
        .filter((platform) =>
            withOrWithoutComment === ReviewReplyAutomationComment.WITH_COMMENT
                ? platform.replyOptions.every((replyOption) =>
                      [
                          ReplyOption.YES,
                          ReplyOption.WITH_TIME_LIMIT,
                          ReplyOption.WITH_COMMENT_ONLY,
                          ReplyOption.WITH_CLIENT_ID,
                          ReplyOption.WITH_REVIEWER_NAME,
                          ReplyOption.ON_THE_PLATFORM,
                          ReplyOption.BY_MAIL,
                          ReplyOption.WITH_MANUAL_FALLBACK,
                      ].includes(replyOption)
                  )
                : platform.replyOptions.every((replyOption) =>
                      [
                          ReplyOption.YES,
                          ReplyOption.WITH_TIME_LIMIT,
                          ReplyOption.WITH_CLIENT_ID,
                          ReplyOption.WITH_REVIEWER_NAME,
                          ReplyOption.ON_THE_PLATFORM,
                          ReplyOption.BY_MAIL,
                          ReplyOption.WITH_MANUAL_FALLBACK,
                      ].includes(replyOption)
                  )
        )
        .map((platform) => platform.key);

/**
 * Return a list of platforms that can have shouldValidateAiBeforeSend set to false
 * (meaning that the platform can attempt to automatically reply to reviews without human validation;
 * platforms with WITH_MANUAL_FALLBACK may fall back to a manual reply if the API call fails)
 */
export const getPlatformKeysAutomatableWithoutValidation = (withOrWithoutComment: ReviewReplyAutomationComment): PlatformKey[] =>
    getPlatformDefinitions()
        .filter((platform) =>
            withOrWithoutComment === ReviewReplyAutomationComment.WITH_COMMENT
                ? platform.replyOptions.every((replyOption) =>
                      [
                          ReplyOption.YES,
                          ReplyOption.WITH_TIME_LIMIT,
                          ReplyOption.WITH_COMMENT_ONLY,
                          ReplyOption.WITH_CLIENT_ID,
                          ReplyOption.WITH_REVIEWER_NAME,
                          ReplyOption.BY_MAIL,
                          ReplyOption.WITH_MANUAL_FALLBACK,
                      ].includes(replyOption)
                  )
                : platform.replyOptions.every((replyOption) =>
                      [
                          ReplyOption.YES,
                          ReplyOption.WITH_TIME_LIMIT,
                          ReplyOption.WITH_CLIENT_ID,
                          ReplyOption.WITH_REVIEWER_NAME,
                          ReplyOption.BY_MAIL,
                      ].includes(replyOption)
                  )
        )
        .map((platform) => platform.key);

/**
 * Return a list of platforms that can have shouldValidateAiBeforeSend set to true
 * (meaning that the review reply must be validated by a human before being sent)
 */
export const getPlatformKeysAutomatableWithValidation = getPlatformKeysThatCanBeReplied;

export const canReviewReplyBeModified = (key: string): boolean => {
    const platform = getPlatformDefinition(key);
    if (!platform) {
        return false;
    }
    if (platform.hasReviews && platform.canReviewReplyBeModified) {
        return true;
    }
    return false;
};

export const getPlatformKeysWithReview = (): PlatformKey[] =>
    getPlatformDefinitions()
        .filter((platform) => platform.hasReviews)
        .map((platform) => platform.key);

export const getNonFeatureFlaggedPlatformsWithReviews = (): PlatformKey[] =>
    getNonFeatureFlaggedPlatforms()
        .filter((platform) => platform.hasReviews)
        .map((platform) => platform.key);

export const getPlatformKeysWithRating = (): PlatformKey[] =>
    getPlatformDefinitions()
        .filter((platform) => platform.hasRating)
        .map((platform) => platform.key);

export const hasRatingOutOfTen = (key: string): boolean => getPlatformDefinition(key)?.hasRatingOutOfTen ?? false;

export const hasDelayToReplyReview = (key: string): boolean => !!getPlatformDefinition(key)?.maxReviewsDaysInterval;

export const canHaveMultipleReplies = (key: string): boolean => getPlatformDefinition(key)?.reviewCanHaveMultipleReplies ?? false;

export const getPlatformKeysWithComment = (): PlatformKey[] =>
    getPlatformDefinitions()
        .filter((platform) => platform.hasComments)
        .map((platform) => platform.key);

export const isRepliedOnThePlatform = (key: PlatformKey): boolean => {
    const platform = getPlatformDefinition(key);
    if (!platform) {
        return false;
    }
    return platform.replyOptions.includes(ReplyOption.ON_THE_PLATFORM);
};

export const allowsResponseByMail = (key: PlatformKey): boolean => {
    const platform = getPlatformDefinition(key);
    if (!platform) {
        return false;
    }
    return platform.replyOptions.includes(ReplyOption.BY_MAIL);
};

export const needMailToBeReplied = (key: PlatformKey): boolean => {
    const platform = getPlatformDefinition(key);
    if (!platform) {
        return false;
    }
    return platform.replyOptions.includes(ReplyOption.WITH_CLIENT_ID);
};

export const hasManualFallback = (platformKey: PlatformKey): boolean => {
    const platformDefinition = getPlatformDefinition(platformKey);
    if (!platformDefinition) {
        return false;
    }
    return platformDefinition.replyOptions.includes(ReplyOption.WITH_MANUAL_FALLBACK);
};

export const hasAScheduledRetryPolicy = (platformKey: PlatformKey): boolean => {
    const platformDefinition = getPlatformDefinition(platformKey);
    if (!platformDefinition) {
        return false;
    }
    return platformDefinition.retryPolicy === ReviewRetryPolicy.SCHEDULED;
};

export const hasAwaitingSuccessfulReplyRetryPolicy = (platformKey: PlatformKey): boolean => {
    const platformDefinition = getPlatformDefinition(platformKey);
    if (!platformDefinition) {
        return false;
    }
    return platformDefinition.retryPolicy === ReviewRetryPolicy.AWAITING_SUCCESSFUL_REPLY;
};

export const getReviewComplianceRuleUrl = (platformKey: PlatformKey): string | undefined => {
    const platformDefinition = getPlatformDefinition(platformKey);
    if (!platformDefinition) {
        return undefined;
    }
    return platformDefinition.reviewComplianceRuleUrl;
};

// Messages
export const getPlatformKeysWithMessages = (): PlatformKey[] =>
    getPlatformDefinitions()
        .filter((platform) => platform.hasMessages)
        .map((platform) => platform.key);

export const getSocialPlatformKeysWithMessages = (): PlatformKey[] =>
    getPlatformDefinitions()
        .filter((platform) => platform.hasMessages && platform.category === PlatformCategory.SOCIAL)
        .map((platform) => platform.key);

// Totems
export const getPlatformKeysNotRatedForTotems = (): NfcsPlatformKey[] => [
    ...getPlatformDefinitions()
        .filter((platform) => !platform.isRatedForTotems)
        .map((platform) => platform.key),
    NfcsPlatformKey.WHEEL_OF_FORTUNE_PLATFORM_KEY,
];

// Wheel of Fortune
export const getPlatformKeysForWheelOfFortuneRedirection = (): PlatformKey[] =>
    getPlatformDefinitions()
        .filter((platform) => platform.hasWheelOfFortuneRedirectionForReview || platform.hasWheelOfFortuneRedirectionForFollow)
        .map((platform) => platform.key);

export const getPlatformKeysForWheelOfFortuneRedirectionForReview = (): PlatformKey[] =>
    getPlatformDefinitions()
        .filter((platform) => platform.hasWheelOfFortuneRedirectionForReview)
        .map((platform) => platform.key);

export const getPlatformKeysForWheelOfFortuneRedirectionForFollow = (): PlatformKey[] =>
    getPlatformDefinitions()
        .filter((platform) => platform.hasWheelOfFortuneRedirectionForFollow)
        .map((platform) => platform.key);

export const changePlatformUrlDomain = (url: string | undefined, platformKey: PlatformKey, langs: string[]): string | undefined => {
    const domainMap = getPlatformDefinition(platformKey)?.domainMap;

    if (!domainMap) {
        return url;
    }

    if (!url || !url.includes(platformKey)) {
        return url;
    }

    // Find first language to match the domain map
    const isoLanguageCodes = mapLanguageStringsToISO(langs);
    const matchingLanguage = isoLanguageCodes.find((lang) => !!domainMap[lang]);

    if (!matchingLanguage) {
        return url;
    }

    return changeUrlDomain(url, domainMap[matchingLanguage] as string);
};

export const checkSocialNetworkUrl = (url: string, socialNetworkKey: SocialNetworkKey): boolean => {
    switch (socialNetworkKey) {
        case SocialNetworkKey.FACEBOOK:
            return isValidFacebookUrl(url);
        case SocialNetworkKey.INSTAGRAM:
            return isValidInstagramUrl(url);
        case SocialNetworkKey.TIKTOK:
            return isValidTiktokUrl(url);
        case SocialNetworkKey.LINKEDIN:
            return isValidLinkedinUrl(url);
        case SocialNetworkKey.PINTEREST:
            return isValidPinterestUrl(url);
        case SocialNetworkKey.YOUTUBE:
            return isValidYoutubeUrl(url);
        case SocialNetworkKey.X:
            return isValidXUrl(url);
        default:
            return false;
    }
};

export const checkSocialNetworkUrls = (socialNetworkUrls: { key: SocialNetworkKey; url: string }[]): boolean =>
    socialNetworkUrls.every(({ key, url }) => checkSocialNetworkUrl(url, key));

export const formatSocialNetworkUrls = (
    socialNetworkUrls: { key: SocialNetworkKey; url: string }[]
): { key: SocialNetworkKey; url: string }[] => {
    const formatSocialNetworkUrl: Record<SocialNetworkKey, (url: string) => string | null> = {
        [SocialNetworkKey.FACEBOOK]: transformFacebookUrl,
        [SocialNetworkKey.INSTAGRAM]: transformInstagramUrl,
        [SocialNetworkKey.LINKEDIN]: transformLinkedinUrl,
        [SocialNetworkKey.PINTEREST]: transformPinterestUrl,
        [SocialNetworkKey.TIKTOK]: transformTiktokUrl,
        [SocialNetworkKey.X]: transformXUrl,
        [SocialNetworkKey.YOUTUBE]: transformYoutubeUrl,
    };

    return socialNetworkUrls
        .map(({ key, url }) => {
            const formattedUrl = formatSocialNetworkUrl[key]?.(url);

            if (!formattedUrl) {
                return undefined;
            }

            return { key, url: formattedUrl };
        })
        .filter(isNotNil);
};
